/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  input,
  Input,
  NgZone,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  forwardRef,
  Optional,
  Inject,
  DoCheck,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormControl,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { CustomInputComponent } from '../custom-input/custom-input.component';
import { GOOGLE_MAPS_KEY } from '../../../config/google.config';
import { CustomErrorDisplayComponent } from '../custom-error-display/custom-error-display.component';

// Define a type for the global window object to include google.maps
interface WindowWithGoogleMaps extends Window {
  google?: typeof google;
}

// Singleton promise to ensure the API script is loaded only once.
let apiLoadedPromise: Promise<void> | null = null;

export interface LocationData {
  position: google.maps.LatLngLiteral;
  address: string;
}

@Component({
  selector: 'lib-custom-map-input',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    GoogleMap,
    MapMarker,
    CustomInputComponent,
    CustomErrorDisplayComponent,
    TranslateModule,
  ],
  templateUrl: './custom-map-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomMapInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomMapInputComponent
  implements ControlValueAccessor, OnDestroy, AfterViewInit, DoCheck
{
  @Input() selectionType: 'single' | 'multiple' = 'single';
  label = input<string>('');
  id = input<string>('');
  name = input<string>('');
  errors = input<{ [key: string]: string }>({});
  enableSearch = input<boolean>(true);
  enableTotalLocations = input<boolean>(false);

  // This is a user-specific API key. It should be provided by the user.
  //private readonly _googleMapsApiKey = 'AIzaSyCYEJBfj1sOQQrNz5L8Rz8xYv_WVgjqJUg'; // <-- IMPORTANT: INSERT YOUR API KEY HERE

  isGoogleMapsApiLoaded = false;
  isDisabled = false;
  locationControls = new FormArray<FormControl<string | null>>([]);
  searchControl = new FormControl('');
  private readonly _searchResultsSubject = new BehaviorSubject<google.maps.places.PlaceResult[]>(
    []
  );
  readonly searchResults$ = this._searchResultsSubject.asObservable();
  markers: google.maps.MarkerOptions[] = [];
  minLocations: number | null = null;
  maxLocations: number | null = null;
  ngControl: NgControl | null = null;
  mapOptions: google.maps.MapOptions = {
    center: { lat: 21.8792415, lng: -102.3305655 }, // Centered on Mexico
    zoom: 12,
    mapTypeControl: false,
    streetViewControl: false,
  };

  MAX_LOCATIONS = 10;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  @ViewChild(GoogleMap) private _map: GoogleMap | undefined;
  private _placesService!: google.maps.places.PlacesService;
  private _geocoder!: google.maps.Geocoder;
  private _attributionContainerId: string = `places-container-${Math.random()
    .toString(36)
    .substring(2, 9)}`;

  private readonly _subscriptions = new Subscription();

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly _ngZone: NgZone,
    private readonly _injector: Injector,
    private readonly _translate: TranslateService,
    @Optional() @Inject(GOOGLE_MAPS_KEY) private _googleMapsApiKey: string
  ) {}

  ngAfterViewInit(): void {
    this.ngControl = this._injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
      if (this.ngControl.control) {
        this._extractMinMaxValidators(this.ngControl.control);
      }
    }

    if (!this._googleMapsApiKey) {
      console.error(
        'Google Maps API key is not provided. Please set it in custom-map-input.component.ts'
      );
      return;
    }
    this._loadApi();

    this._subscriptions.add(
      this.searchControl.valueChanges
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          switchMap((query) => (query ? this._searchPlaces(query) : of([]))),
          catchError((err) => {
            console.error('Error searching places:', err);
            return of([]);
          })
        )
        .subscribe((results) => {
          this._searchResultsSubject.next(results);
        })
    );

    this._subscriptions.add(
      this.locationControls.valueChanges.subscribe(() => {
        this._updateValue();
      })
    );
  }

  ngDoCheck(): void {
    if (this.ngControl?.touched) {
      this.searchControl.markAsTouched();
    }
  }

  onSearchCleared(): void {
    if (this.selectionType === 'single') {
      this.removeLocation(0);
      this.searchControl.setValue('', { emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    const container = document.getElementById(this._attributionContainerId);
    if (container) {
      container.remove();
    }
  }

  writeValue(value: LocationData | LocationData[] | null): void {
    this.markers = [];
    this.locationControls.clear({ emitEvent: false });

    const locations: LocationData[] = [];
    if (this.selectionType === 'single' && value) {
      if (value && 'position' in value && 'address' in value) {
        locations.push(value as LocationData);
      }
    } else if (this.selectionType === 'multiple' && Array.isArray(value)) {
      locations.push(...value.filter((v) => v && v.position && v.address));
    }

    if (locations.length > 0) {
      this.markers = locations.map((loc) => ({
        position: loc.position,
        draggable: !this.isDisabled,
      }));
      locations.forEach((loc) => {
        this.locationControls.push(new FormControl(loc.address), {
          emitEvent: false,
        });
      });
    }

    this._updateSearchControlState();
    this._updateMapBounds();

    // If single selection, populate search control with the address
    if (this.selectionType === 'single' && locations.length > 0) {
      this.searchControl.setValue(locations[0].address, { emitEvent: false });
    }

    this._cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
    // When touched is called, explicitly check validity to update UI
    if (this.ngControl) {
      this._cdr.markForCheck();
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;

    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false });
    } else {
      this._updateSearchControlState(); // This will re-enable if appropriate
    }

    // Update marker draggability
    this.markers = this.markers.map((marker) => ({
      ...marker,
      draggable: !isDisabled,
    }));

    this._cdr.markForCheck();
  }

  selectPlace(place: google.maps.places.PlaceResult): void {
    if (!place.geometry?.location) return;

    const position = place.geometry.location.toJSON();
    const address = place.formatted_address;

    this._addMarker(position, address);

    // Update search control with address if single, otherwise clear
    if (this.selectionType === 'single') {
      this.searchControl.setValue(address || '', { emitEvent: false });
    } else {
      this.searchControl.setValue('', { emitEvent: false });
    }

    this._searchResultsSubject.next([]);
  }

  mapClick(event: google.maps.MapMouseEvent): void {
    if (this.isDisabled) return;
    if (event.latLng) {
      this._addMarker(event.latLng.toJSON());
    }
  }

  onMarkerDragEnd(event: { latLng: google.maps.LatLng | null | undefined }, index: number): void {
    if (event.latLng) {
      const newPosition = event.latLng.toJSON();
      const oldMarker = this.markers[index];
      this.markers[index] = { ...oldMarker, position: newPosition };
      this._updateValue();
      this._reverseGeocode(newPosition, index);
      this._cdr.markForCheck();
    }
  }

  removeLocation(index: number): void {
    this._removeMarker(index);
  }

  centerMapOnLocation(index: number): void {
    if (!this._map || !this._map.googleMap || !this.markers[index]?.position) {
      return;
    }
    const position = this.markers[index].position;
    if (position) {
      this._map.googleMap.setCenter(position);
      this._map.googleMap.setZoom(15);
    }
  }

  private _addMarker(position: google.maps.LatLngLiteral, address?: string | null): void {
    if (this.selectionType === 'single' && this.markers.length >= 1) {
      this.markers = [];
      this.locationControls.clear({ emitEvent: false });
    }

    this.markers.push({ position, draggable: !this.isDisabled });

    const addressToDisplay =
      address || this._translate.instant('SHARED.INPUTS_CUSTOM-MAP-INPUT.LOADING_ADDRESS');
    this.locationControls.push(new FormControl(addressToDisplay), { emitEvent: false });

    if (address) {
      this._updateValue(); // We have all data, update the form value
    } else {
      this._reverseGeocode(position, this.locationControls.length - 1); // Fallback to API call
    }

    this._updateSearchControlState();
    this._updateMapBounds();
  }

  private _removeMarker(index: number): void {
    if (index >= 0 && index < this.markers.length) {
      this.markers.splice(index, 1);
      this.locationControls.removeAt(index);
      this._updateValue();
      this._updateSearchControlState();
      this._updateMapBounds();
    }
  }

  private _updateValue(): void {
    if (this.selectionType === 'single') {
      if (this.markers.length > 0 && this.markers[0].position) {
        const locationData: LocationData = {
          position: this.markers[0].position as google.maps.LatLngLiteral,
          address: this.locationControls.at(0)?.value || '',
        };
        this.onChange(locationData);
      } else {
        this.onChange(null);
      }
    } else {
      const locationsData: LocationData[] = this.markers
        .map((marker, index) => {
          if (!marker.position) return null;
          return {
            position: marker.position as google.maps.LatLngLiteral,
            address: this.locationControls.at(index)?.value || '',
          };
        })
        .filter((data): data is LocationData => data !== null);
      this.onChange(locationsData);
    }
    this.onTouched();
  }

  private _updateSearchControlState(): void {
    if (this.isDisabled) {
      this.searchControl.disable({ emitEvent: false });
      return;
    }

    const isMultipleSelectionFull =
      this.selectionType === 'multiple' &&
      this.maxLocations !== null &&
      this.markers.length >= this.maxLocations;

    if (isMultipleSelectionFull) {
      this.searchControl.disable({ emitEvent: false });
    } else {
      this.searchControl.enable({ emitEvent: false });
    }
  }

  private _reverseGeocode(position: google.maps.LatLngLiteral, index: number): void {
    if (!this._geocoder) return;

    this._geocoder.geocode({ location: position }, (results, status) => {
      this._ngZone.run(() => {
        if (status === 'OK' && results && results[0]) {
          this.locationControls
            .at(index)
            ?.setValue(results[0].formatted_address, { emitEvent: false });

          // If single selection, also update the search control
          if (this.selectionType === 'single' && index === 0) {
            this.searchControl.setValue(results[0].formatted_address, { emitEvent: false });
          }
        } else {
          const fallbackAddress = this._translate.instant(
            'SHARED.INPUTS_CUSTOM-MAP-INPUT.LAT_LNG_FALLBACK',
            { lat: position.lat.toFixed(4), lng: position.lng.toFixed(4) }
          );
          this.locationControls.at(index).setValue(fallbackAddress, { emitEvent: false });

          if (this.selectionType === 'single' && index === 0) {
            this.searchControl.setValue(fallbackAddress, { emitEvent: false });
          }
          console.warn(`Reverse geocode was not successful: ${status}`);
        }
        this._updateValue();
        this._cdr.markForCheck();
      });
    });
  }

  private _loadApi(): void {
    if (!apiLoadedPromise) {
      apiLoadedPromise = new Promise((resolve, reject) => {
        if ((window as WindowWithGoogleMaps).google?.maps) {
          return resolve();
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this._googleMapsApiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = (err) => reject(err);
        document.head.appendChild(script);
      });
    }

    apiLoadedPromise
      .then(() => {
        this._ngZone.run(() => {
          this.isGoogleMapsApiLoaded = true;
          this._initializeServices();
          this._cdr.markForCheck();
        });
      })
      .catch((err) => {
        console.error('Error loading Google Maps API', err);
      });
  }

  private _initializeServices(): void {
    const container = document.createElement('div');
    container.id = this._attributionContainerId;
    container.style.display = 'none';
    document.body.appendChild(container);

    this._placesService = new google.maps.places.PlacesService(container);
    this._geocoder = new google.maps.Geocoder();
  }

  private _updateAddressesFromPositions(): void {
    this.locationControls.clear();
    this.markers.forEach((marker, index) => {
      this.locationControls.push(new FormControl('Loading address...'));
      if (marker.position) {
        this._reverseGeocode(marker.position as google.maps.LatLngLiteral, index);
      }
    });
  }

  private _updateMapBounds(): void {
    if (!this._map || !this._map.googleMap) return;

    if (this.markers.length === 0) {
      this._map.googleMap.setCenter(this.mapOptions.center!);
      this._map.googleMap.setZoom(this.mapOptions.zoom!);
      return;
    }

    if (this.markers.length === 1 && this.markers[0].position) {
      this._map.googleMap.setCenter(this.markers[0].position);
      this._map.googleMap.setZoom(15);
    } else {
      const bounds = new google.maps.LatLngBounds();
      this.markers.forEach((marker) => {
        if (marker.position) {
          bounds.extend(marker.position);
        }
      });
      if (!bounds.isEmpty()) {
        this._map.fitBounds(bounds);
      }
    }
  }

  private _searchPlaces(query: string): Observable<google.maps.places.PlaceResult[]> {
    if (!this._placesService) {
      return of([]);
    }

    const request: google.maps.places.TextSearchRequest = {
      query,
    };

    if (this._map && this._map.getBounds()) {
      request.bounds = this._map.getBounds() ?? undefined;
    }

    return new Observable((observer) => {
      this._placesService.textSearch(request, (results, status) => {
        this._ngZone.run(() => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            observer.next(results.slice(0, 5)); // Limit results
            observer.complete();
          } else {
            observer.error(status);
          }
        });
      });
    });
  }

  private _extractMinMaxValidators(control: AbstractControl): void {
    // Min
    const errorsForShort: any = control.validator?.({ value: [1] } as any);
    const min = errorsForShort?.minlength?.requiredLength ?? 0;

    // Max
    const errorsForLong: any = control.validator?.({
      value: new Array(this.MAX_LOCATIONS).fill(1),
    } as any);

    const max = errorsForLong?.maxlength?.requiredLength ?? this.MAX_LOCATIONS;

    this.minLocations = min;
    this.maxLocations = this.selectionType === 'single' ? 1 : max;

    if (this.selectionType === 'single' && this.minLocations !== null && this.minLocations > 0) {
      this.searchControl.addValidators(Validators.required);
      this.searchControl.updateValueAndValidity({ emitEvent: false });
    }
  }
}
